import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calculator, BookOpen, Plus, Trash2, Pencil, Download, Save, FileText, X, Search, Folder, Package
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const downloadPdf = async (url, name) => {
  try {
    const res = await axios.get(url, { responseType: "blob" });
    const u = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = u; a.download = name || "documento.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(u);
  } catch { toast.error("Errore download PDF"); }
};

const downloadXlsx = async (url, name) => {
  try {
    const res = await axios.get(url, { responseType: "blob" });
    const u = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a");
    a.href = u; a.download = name || "documento.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(u);
  } catch { toast.error("Errore download Excel"); }
};


// ====================== VOCE DIALOG ======================
const VoceDialog = ({ open, onOpenChange, voce, onSave }) => {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open) setForm(voce || { codice: "", descrizione: "", unita_misura: "cad", prezzo_unitario: 0, categoria: "altro", note: "" });
  }, [open, voce]);

  const handleSave = async () => {
    if (!form.descrizione?.trim()) { toast.error("Descrizione obbligatoria"); return; }
    await onSave({ ...form, prezzo_unitario: parseFloat(form.prezzo_unitario) || 0 });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="voce-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Modifica Voce" : "Nuova Voce di Lavoro"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Codice</Label><Input value={form.codice || ""} onChange={e => setForm(p => ({ ...p, codice: e.target.value }))} placeholder="Es. TIN-01" /></div>
            <div><Label>Categoria</Label><Input value={form.categoria || ""} onChange={e => setForm(p => ({ ...p, categoria: e.target.value }))} placeholder="tinteggiatura, demolizione..." /></div>
          </div>
          <div><Label>Descrizione *</Label><Input value={form.descrizione || ""} onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))} data-testid="voce-descrizione" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Unità di misura</Label><Input value={form.unita_misura || ""} onChange={e => setForm(p => ({ ...p, unita_misura: e.target.value }))} placeholder="m², cad, m³..." /></div>
            <div><Label>Prezzo unitario (€)</Label><Input type="number" step="0.01" value={form.prezzo_unitario || 0} onChange={e => setForm(p => ({ ...p, prezzo_unitario: e.target.value }))} data-testid="voce-prezzo" /></div>
          </div>
          <div><Label>Note</Label><Textarea rows={2} value={form.note || ""} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleSave} data-testid="btn-save-voce"><Save className="h-4 w-4 mr-1" /> Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ====================== INV LIST PICKER (embedded in ComputoDialog) ======================
const InventoryPicker = ({ open, onOpenChange, authToken, progettoId, onImport }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyProgetto, setOnlyProgetto] = useState(!!progettoId);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const url = onlyProgetto && progettoId
      ? `${API}/design/inventory-lists?token=${authToken}&progetto_id=${progettoId}`
      : `${API}/design/inventory-lists?token=${authToken}`;
    axios.get(url).then(r => setLists(r.data || [])).catch(() => toast.error("Errore liste"))
      .finally(() => setLoading(false));
  }, [open, onlyProgetto, progettoId, authToken]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="cm-inv-picker">
        <DialogHeader>
          <DialogTitle>Importa da liste Inventario</DialogTitle>
          <DialogDescription>Aggiungi righe al computo dalle liste inventario salvate.</DialogDescription>
        </DialogHeader>
        {progettoId && (
          <label className="flex items-center gap-2 text-xs px-2 py-1 bg-gray-50 rounded border cursor-pointer w-fit">
            <input type="checkbox" checked={onlyProgetto} onChange={e => setOnlyProgetto(e.target.checked)} />
            Solo liste di questo progetto
          </label>
        )}
        <div className="space-y-2">
          {loading ? <p className="text-center py-6 text-gray-500 text-sm">Caricamento...</p> :
           lists.length === 0 ? <p className="text-center py-8 text-gray-500 text-sm">Nessuna lista trovata.</p> :
           lists.map(lst => {
             const cnt = (lst.oggetti || []).length;
             const totale = (lst.oggetti || []).reduce((s, o) => s + (parseFloat(o.prezzo_stimato) || 0) * (parseInt(o.quantita) || 1), 0);
             return (
               <Card key={lst.id} className="border-l-4 border-l-slate-500">
                 <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                   <div>
                     <div className="font-semibold">{lst.nome}</div>
                     <div className="text-xs text-gray-500">{cnt} oggetti • Totale € {totale.toFixed(2)}</div>
                   </div>
                   <Button size="sm" onClick={() => onImport(lst)} data-testid={`cm-inv-import-${lst.id}`}><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
                 </CardContent>
               </Card>
             );
           })
          }
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ====================== COMPUTO DIALOG (duplicato semplificato) ======================
const ComputoDialog = ({ open, onOpenChange, computo, voci, onSave, authToken }) => {
  const [form, setForm] = useState({});
  const [invPickerOpen, setInvPickerOpen] = useState(false);

  useEffect(() => {
    if (open) setForm(computo || { nome: "", descrizione: "", righe: [], note: "", progetto_id: "" });
  }, [open, computo]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const addRiga = () => setField("righe", [...(form.righe || []), { id: `r-${Date.now()}`, descrizione: "", unita_misura: "cad", quantita: 1, prezzo_unitario: 0, note: "" }]);
  const setRigaField = (i, k, v) => setField("righe", form.righe.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeRiga = (i) => setField("righe", form.righe.filter((_, idx) => idx !== i));

  const importVoceFromCatalogo = (i, voceId) => {
    const v = voci.find(x => x.id === voceId);
    if (!v) return;
    setField("righe", form.righe.map((r, idx) => idx === i ? {
      ...r, voce_id: v.id, descrizione: v.descrizione, unita_misura: v.unita_misura, prezzo_unitario: v.prezzo_unitario,
    } : r));
  };

  const importFromInvList = (lst) => {
    const newRighe = (lst.oggetti || []).map(o => ({
      id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      voce_id: "",
      descrizione: (o.descrizione || "").slice(0, 200),
      unita_misura: "cad",
      quantita: parseInt(o.quantita) || 1,
      prezzo_unitario: parseFloat(o.prezzo_stimato) || 0,
      note: o.fornitore ? `Fornitore: ${o.fornitore}` : "",
    }));
    setField("righe", [...(form.righe || []), ...newRighe]);
    setInvPickerOpen(false);
    toast.success(`Importate ${newRighe.length} righe da "${lst.nome}"`);
  };

  const totale = (form.righe || []).reduce((s, r) => s + (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0), 0);

  const handleSave = async () => {
    if (!form.nome?.trim()) { toast.error("Nome obbligatorio"); return; }
    await onSave({ ...form });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="cm-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? "Modifica Computo" : "Nuovo Computo Metrico"}</DialogTitle>
          <DialogDescription>Inserisci le voci di lavoro con quantità e prezzi</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Nome *</Label><Input value={form.nome || ""} onChange={e => setField("nome", e.target.value)} data-testid="cm-nome" /></div>
          <div><Label>Descrizione</Label><Input value={form.descrizione || ""} onChange={e => setField("descrizione", e.target.value)} /></div>
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Voci ({(form.righe || []).length})</h3>
          <div className="flex gap-2">
            <Button size="sm" type="button" variant="outline" onClick={() => setInvPickerOpen(true)} data-testid="cm-import-inv" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <Package className="h-4 w-4 mr-1" /> Importa da Inventario
            </Button>
            <Button size="sm" type="button" variant="outline" onClick={addRiga}><Plus className="h-4 w-4 mr-1" /> Aggiungi riga</Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs">
              <tr>
                <th className="p-1">Da catalogo</th>
                <th className="p-1">Descrizione</th>
                <th className="p-1">U.M.</th>
                <th className="p-1">Qta</th>
                <th className="p-1">€ unit.</th>
                <th className="p-1">Totale</th>
                <th className="p-1"></th>
              </tr>
            </thead>
            <tbody>
              {(form.righe || []).map((r, i) => {
                const tot = (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0);
                return (
                  <tr key={r.id || i} className="border-t">
                    <td className="p-1">
                      <Select value={r.voce_id || "none"} onValueChange={(v) => v !== "none" && importVoceFromCatalogo(i, v)}>
                        <SelectTrigger className="h-8 text-xs w-44"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Manuale —</SelectItem>
                          {(voci || []).map(v => <SelectItem key={v.id} value={v.id}>{v.codice} - {v.descrizione.substring(0, 40)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1"><Input value={r.descrizione || ""} onChange={e => setRigaField(i, "descrizione", e.target.value)} className="h-8 text-xs" /></td>
                    <td className="p-1"><Input value={r.unita_misura || ""} onChange={e => setRigaField(i, "unita_misura", e.target.value)} className="h-8 text-xs w-16" /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={r.quantita} onChange={e => setRigaField(i, "quantita", e.target.value)} className="h-8 text-xs w-20" /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={r.prezzo_unitario} onChange={e => setRigaField(i, "prezzo_unitario", e.target.value)} className="h-8 text-xs w-24" /></td>
                    <td className="p-1 font-medium">€ {tot.toFixed(2)}</td>
                    <td className="p-1"><Button size="sm" variant="ghost" onClick={() => removeRiga(i)}><X className="h-3 w-3" /></Button></td>
                  </tr>
                );
              })}
              {(form.righe || []).length === 0 && (
                <tr><td colSpan="7" className="text-center text-gray-500 py-6 text-xs">Nessuna riga.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td colSpan="5" className="p-2 font-bold text-right">TOTALE COMPUTO</td>
                <td colSpan="2" className="p-2 font-bold text-slate-800 text-base">€ {totale.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div><Label>Note</Label><Textarea rows={2} value={form.note || ""} onChange={e => setField("note", e.target.value)} /></div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleSave} data-testid="cm-btn-save"><Save className="h-4 w-4 mr-1" /> Salva</Button>
        </DialogFooter>
      </DialogContent>
      <InventoryPicker
        open={invPickerOpen}
        onOpenChange={setInvPickerOpen}
        authToken={authToken}
        progettoId={form.progetto_id || ""}
        onImport={importFromInvList}
      />
    </Dialog>
  );
};


// ====================== MAIN ======================
export default function ComputoMetrico({ authToken }) {
  const [subTab, setSubTab] = useState("computi");
  const [voci, setVoci] = useState([]);
  const [computi, setComputi] = useState([]);
  const [search, setSearch] = useState("");
  const [filtroCat, setFiltroCat] = useState("");

  const [voceDialogOpen, setVoceDialogOpen] = useState(false);
  const [editingVoce, setEditingVoce] = useState(null);

  const [cmDialogOpen, setCmDialogOpen] = useState(false);
  const [editingCm, setEditingCm] = useState(null);

  const loadVoci = async () => {
    try {
      const r = await axios.get(`${API}/computo/voci?token=${authToken}`);
      setVoci(r.data || []);
    } catch { /* ignore */ }
  };

  const loadComputi = async () => {
    try {
      const r = await axios.get(`${API}/computo/computi?token=${authToken}`);
      setComputi(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!authToken) return;
    loadVoci(); loadComputi();
  }, [authToken]);

  const categorie = useMemo(() => {
    const set = new Set();
    voci.forEach(v => v.categoria && set.add(v.categoria));
    return Array.from(set).sort();
  }, [voci]);

  const filteredVoci = useMemo(() => {
    const s = search.toLowerCase().trim();
    return voci.filter(v => {
      if (filtroCat && v.categoria !== filtroCat) return false;
      if (s && !(v.descrizione.toLowerCase().includes(s) || v.codice.toLowerCase().includes(s))) return false;
      return true;
    });
  }, [voci, search, filtroCat]);

  const handleSaveVoce = async (v) => {
    try {
      if (v.id) await axios.put(`${API}/computo/voci/${v.id}?token=${authToken}`, v);
      else await axios.post(`${API}/computo/voci?token=${authToken}`, v);
      setVoceDialogOpen(false);
      loadVoci();
      toast.success("Voce salvata");
    } catch { toast.error("Errore"); }
  };
  const handleDeleteVoce = async (id) => {
    if (!window.confirm("Eliminare la voce?")) return;
    try {
      await axios.delete(`${API}/computo/voci/${id}?token=${authToken}`);
      loadVoci();
    } catch { toast.error("Errore"); }
  };

  const handleSaveCm = async (c) => {
    try {
      if (c.id) await axios.put(`${API}/computo/computi/${c.id}?token=${authToken}`, c);
      else await axios.post(`${API}/computo/computi?token=${authToken}`, c);
      setCmDialogOpen(false);
      loadComputi();
      toast.success("Computo salvato");
    } catch { toast.error("Errore"); }
  };
  const handleDeleteCm = async (id) => {
    if (!window.confirm("Eliminare il computo?")) return;
    try {
      await axios.delete(`${API}/computo/computi/${id}?token=${authToken}`);
      loadComputi();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 text-slate-700">
            <Calculator className="h-6 w-6" /> Computo Metrico
          </h2>
          <p className="text-sm text-gray-500">Catalogo voci di lavoro e stima dei costi per le tue trasformazioni</p>
        </div>
      </div>

      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="computi" data-testid="cm-tab-computi" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white"><FileText className="h-4 w-4 mr-1" /> Computi</TabsTrigger>
          <TabsTrigger value="catalogo" data-testid="cm-tab-catalogo" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white"><BookOpen className="h-4 w-4 mr-1" /> Catalogo Voci</TabsTrigger>
        </TabsList>

        {/* COMPUTI */}
        <TabsContent value="computi" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Elenco Computi ({computi.length})</CardTitle>
                <Button onClick={() => { setEditingCm(null); setCmDialogOpen(true); }} className="bg-slate-700 hover:bg-slate-800" data-testid="cm-btn-new"><Plus className="h-4 w-4 mr-1" /> Nuovo Computo</Button>
              </div>
            </CardHeader>
            <CardContent>
              {computi.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calculator className="h-14 w-14 mx-auto opacity-30 mb-2" />
                  <p>Nessun computo. Crea il primo per stimare i lavori.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {computi.map(c => {
                    const tot = (c.righe || []).reduce((s, r) => s + (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0), 0);
                    return (
                      <Card key={c.id} className="border-l-4 border-l-slate-500">
                        <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <div className="font-semibold">{c.nome}</div>
                            <div className="text-xs text-gray-500">
                              {(c.righe || []).length} voci • {c.progetto_id && <Badge variant="secondary" className="mr-1"><Folder className="h-3 w-3 inline mr-1" /> Collegato a progetto</Badge>}
                            </div>
                            {c.descrizione && <div className="text-xs text-gray-600 mt-1">{c.descrizione}</div>}
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Totale</div>
                            <div className="text-xl font-bold text-slate-800">€ {tot.toFixed(2)}</div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => downloadPdf(`${API}/computo/computi/${c.id}/pdf?token=${authToken}`, `computo_${c.nome}.pdf`)} title="PDF" data-testid={`cm-btn-pdf-${c.id}`}><Download className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => downloadXlsx(`${API}/computo/computi/${c.id}/excel?token=${authToken}`, `computo_${c.nome}.xlsx`)} title="Excel" data-testid={`cm-btn-xlsx-${c.id}`} className="text-emerald-700"><FileText className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingCm(c); setCmDialogOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteCm(c.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CATALOGO */}
        <TabsContent value="catalogo" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Catalogo Voci di Lavoro ({filteredVoci.length}/{voci.length})</CardTitle>
                <Button onClick={() => { setEditingVoce(null); setVoceDialogOpen(true); }} className="bg-slate-700 hover:bg-slate-800" data-testid="cm-btn-new-voce"><Plus className="h-4 w-4 mr-1" /> Nuova Voce</Button>
              </div>
              <CardDescription>Voci di lavoro standard precompilate, modificabili in qualunque momento.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input className="pl-8" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca per codice o descrizione..." />
                </div>
                <Select value={filtroCat || "all"} onValueChange={(v) => setFiltroCat(v === "all" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Tutte le categorie" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le categorie</SelectItem>
                    {categorie.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-2 py-2 text-xs">Codice</th>
                      <th className="px-2 py-2 text-xs">Descrizione</th>
                      <th className="px-2 py-2 text-xs">U.M.</th>
                      <th className="px-2 py-2 text-xs">€ Unit.</th>
                      <th className="px-2 py-2 text-xs">Categoria</th>
                      <th className="px-2 py-2 text-xs text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVoci.map(v => (
                      <tr key={v.id} className="border-t hover:bg-gray-50">
                        <td className="px-2 py-1 font-mono text-xs">{v.codice}</td>
                        <td className="px-2 py-1">{v.descrizione}</td>
                        <td className="px-2 py-1">{v.unita_misura}</td>
                        <td className="px-2 py-1 font-medium">€ {(v.prezzo_unitario || 0).toFixed(2)}</td>
                        <td className="px-2 py-1"><Badge variant="secondary" className="text-xs">{v.categoria}</Badge></td>
                        <td className="px-2 py-1 text-right">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingVoce(v); setVoceDialogOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteVoce(v.id)}><Trash2 className="h-3 w-3" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <VoceDialog open={voceDialogOpen} onOpenChange={setVoceDialogOpen} voce={editingVoce} onSave={handleSaveVoce} />
      <ComputoDialog open={cmDialogOpen} onOpenChange={setCmDialogOpen} computo={editingCm} voci={voci} onSave={handleSaveCm} authToken={authToken} />
    </div>
  );
}
